<template>
    <div class="CFormInput__wrapper relative">
        <span
            v-if="hasPrefix"
            class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-txt-body"
        >
            <slot name="prefix">
                <CIcon :name="prefixIcon!" class="custom-icon size-5" />
            </slot>
        </span>

        <input
            ref="inputRef"
            :aria-label="ariaLabel"
            :aria-invalid="invalid ? 'true' : 'false'"
            :aria-required="required ? 'true' : 'false'"
            :aria-describedby="ariaDescribedBy"
            class="CFormInput input"
            :class="[
                inputClass,
                {
                    'input--invalid': invalid,
                    'input--disabled': disabled,
                    'pl-10!': hasPrefix,
                    'pr-10!': hasSuffix
                }
            ]"
            :style="inputStyle"
            :disabled="disabled"
            :inputmode="type === 'tel' ? 'numeric' : undefined"
            :max="maxValue"
            :min="minValue"
            :placeholder="placeholder"
            :type="type"
            :value="modelValue"
            v-bind="inputAttrs"
            @change="handleChange"
            @input="handleInput"
        />

        <span
            v-if="hasSuffix"
            class="absolute inset-y-0 right-3 flex items-center text-gray-400 dark:text-txt-body"
            :class="{
                'cursor-pointer': !slots.suffix && isNativeDateOrTimeInput && !disabled
            }"
            @click="handleSuffixClick"
        >
            <slot name="suffix">
                <CIcon :name="suffixIcon!" class="custom-icon pointer-events-none size-5" />
            </slot>
        </span>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, useAttrs, useSlots, type StyleValue } from 'vue'

defineOptions({
    inheritAttrs: false
})

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    placeholder: {
        type: String,
        default: undefined
    },
    minValue: {
        type: [Number, String],
        default: undefined
    },
    maxValue: {
        type: [Number, String],
        default: undefined
    },
    disabled: {
        type: Boolean,
        default: false
    },
    invalid: Boolean,
    required: {
        type: Boolean,
        default: false
    },
    ariaDescribedBy: {
        type: String,
        default: undefined
    },
    prefixIcon: {
        type: String,
        default: undefined
    },
    suffixIcon: {
        type: String,
        default: undefined
    },
    type: {
        type: String,
        default: 'text',
        validator: (value: string) =>
            ['text', 'password', 'email', 'number', 'tel', 'url', 'date', 'time', 'search'].includes(value)
    }
})

const emit = defineEmits(['update:modelValue', 'change'])

const inputRef = ref<HTMLInputElement | null>(null)
const attrs = useAttrs()
const slots = useSlots()

// Better aria-label: use placeholder or type-based label
const ariaLabel = computed(() => {
    if (props.placeholder) return props.placeholder
    const typeLabels: Record<string, string> = {
        email: 'Email address',
        password: 'Password',
        tel: 'Phone number',
        url: 'URL',
        number: 'Number',
        text: 'Text input'
    }
    return typeLabels[props.type] || 'Input field'
})

const hasPrefix = computed(() => Boolean(props.prefixIcon || slots.prefix))
const hasSuffix = computed(() => Boolean(props.suffixIcon || slots.suffix))
const isNativeDateOrTimeInput = computed(() => props.type === 'date' || props.type === 'time')

const inputClass = computed(() => attrs.class)

function isStyleValue(value: unknown): value is StyleValue {
    if (typeof value === 'string' || Array.isArray(value)) {
        return true
    }
    return value !== null && typeof value === 'object'
}

const inputStyle = computed<StyleValue | undefined>(() => {
    return isStyleValue(attrs.style) ? attrs.style : undefined
})

const inputAttrs = computed(() => {
    const { class: _class, style: _style, ...rest } = attrs
    return rest
})

const handleInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    let value = target.value

    // For tel inputs, allow only digits, spaces, and + (no strict formatting)
    if (props.type === 'tel') {
        value = value.replace(/[^\d\s+]/g, '')
        target.value = value
    }

    emit('update:modelValue', value)
}

function handleChange(event: Event) {
    const target = event.target as HTMLInputElement
    emit('change', target.value)
}

// For native date/time inputs the visible calendar/clock icon is just an overlay (the real
// browser picker indicator is transparent and only covers the far-right edge), so clicking it
// did nothing. Explicitly open the native picker on click — with a focus() fallback for browsers
// without showPicker() or when it can't be invoked.
function handleSuffixClick() {
    if (slots.suffix || !isNativeDateOrTimeInput.value || props.disabled) return
    const el = inputRef.value as (HTMLInputElement & { showPicker?: () => void }) | null
    if (!el) return
    if (typeof el.showPicker === 'function') {
        try {
            el.showPicker()
            return
        } catch {
            // showPicker can throw (e.g. not allowed in the current context) — fall back to focus
        }
    }
    el.focus()
}
</script>
